import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { usePartnerCode } from "@/hooks/usePartnerCode";
import { usePartnerCodeRedemptionStatus } from "@/hooks/usePartnerCodeRedemptionStatus";
import { useAuth } from "@/components/auth/AuthProvider";
import { Gift, CheckCircle2, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

export const ManualPartnerCodeRedemption = () => {
  const { userId } = useAuth();
  const [manualCode, setManualCode] = useState("");
  const { redeemCode, isRedeeming } = usePartnerCode();
  const { status, isLoading } = usePartnerCodeRedemptionStatus(manualCode);
  const [redemptionResult, setRedemptionResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  const handleRedeem = async () => {
    if (!userId || !manualCode) return;
    
    try {
      const result = await redeemCode(userId, manualCode);
      setRedemptionResult({
        success: true,
        message: `Successfully activated! You received ${result.credits_granted} credits from ${result.partner_name}`,
      });
      setManualCode("");
    } catch (err) {
      setRedemptionResult({
        success: false,
        message: err instanceof Error ? err.message : "Failed to redeem code",
      });
    }
  };

  const showRedemptionUI = !status?.hasRedeemed || manualCode !== "";

  if (!showRedemptionUI && !redemptionResult) {
    return null;
  }

  return (
    <Card className="p-4">
      <div className="flex items-center gap-2 mb-1">
        <Gift className="h-5 w-5 text-primary" />
        <h2 className="text-lg font-semibold text-foreground">Partner Offers</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Activate partner codes to receive credits or discounts
      </p>
      <div className="space-y-4">
        {redemptionResult && (
          <Alert variant={redemptionResult.success ? "default" : "destructive"}>
            {redemptionResult.success ? (
              <CheckCircle2 className="h-4 w-4" />
            ) : (
              <AlertCircle className="h-4 w-4" />
            )}
            <AlertDescription>{redemptionResult.message}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-2">
          <div className="flex gap-2">
            <Input
              placeholder="Enter partner code"
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value.toUpperCase())}
              disabled={isRedeeming}
            />
            <Button
              onClick={handleRedeem}
              disabled={!manualCode || isRedeeming || isLoading || (status?.hasRedeemed ?? false)}
              variant="secondary"
            >
              {isRedeeming ? "Activating..." : "Activate"}
            </Button>
          </div>

          {status && manualCode && (
            <div className="text-sm">
              {status.hasRedeemed ? (
                <p className="text-muted-foreground flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3" />
                  Already activated on {new Date(status.redemptionDate!).toLocaleDateString()}
                </p>
              ) : status.canRedeem ? (
                <p className="text-green-600 flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3" />
                  Valid code from {status.codeInfo.partner_name}
                </p>
              ) : (
                <p className="text-destructive">Invalid or expired code</p>
              )}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};
